import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Tag } from '@swyftx/aviary/atoms/Tag';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';

import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { getMarginRiskColor } from 'src/lib/margin/utils';

import { MarginAccountHealthBar } from '../MarginHealth';

type Props = {
  className?: string;
};

const MarginAccountDetailsCompact: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation('margin');
  const { profitLossTotal, marginLevel, equity, availableMargin, balanceAvailable, currency, usedMargin, account } =
    useMarginAccount();

  return (
    <FlexLayout
      className={cn('w-full border-b border-color-border-main bg-color-background-surface-primary', className)}
    >
      <OverlayScrollbarsComponent
        options={{ scrollbars: { visibility: 'hidden', autoHide: 'leave', autoHideDelay: 400 } }}
      >
        <FlexLayout alignItems='center' spacing={24} className='py-8'>
          <FlexLayout spacing={4} alignItems='center'>
            <Body color='secondary' size='xsmall' className='whitespace-nowrap pl-16'>
              {t('accountOverview.marginLevel.title')}
            </Body>
            <Numeric
              color={getMarginRiskColor(usedMargin, marginLevel)}
              size='xsmall'
              weight='bold'
              className='whitespace-nowrap'
            >
              {formatCurrency(marginLevel)}%
            </Numeric>
            <div className='hidden w-64 sm:block'>
              <MarginAccountHealthBar className='hidden sm:flex' variant='compact' />
            </div>
          </FlexLayout>

          <FlexLayout spacing={4} alignItems='center'>
            <Body color='secondary' size='xsmall' className='whitespace-nowrap'>
              {t('accountOverview.profitLoss.title')}
            </Body>
            <Numeric
              color={profitLossTotal.gte(0) ? 'success' : 'error'}
              size='xsmall'
              weight='bold'
              className='whitespace-nowrap'
            >
              {formatCurrency(profitLossTotal, currency, { hideCode: false, appendCode: true })}
            </Numeric>
          </FlexLayout>

          {account?.leverage && (
            <FlexLayout spacing={4} alignItems='center'>
              <Body color='secondary' size='xsmall' className='whitespace-nowrap'>
                {t('accountOverview.leverage.title')}
              </Body>
              <Tag label={`${account?.leverage}x`} size='xsmall' />
            </FlexLayout>
          )}

          <FlexLayout spacing={4} alignItems='center'>
            <Body color='secondary' size='xsmall' className='whitespace-nowrap'>
              {t('accountOverview.equity.title')}
            </Body>
            <Numeric color='primary' size='xsmall' weight='bold' className='whitespace-nowrap'>
              {formatCurrency(equity, currency, { hideCode: false, appendCode: true })}
            </Numeric>
          </FlexLayout>

          <FlexLayout spacing={4} alignItems='center'>
            <Body color='secondary' size='xsmall' className='whitespace-nowrap'>
              {t('accountOverview.balance.title')}
            </Body>
            <Numeric color='primary' size='xsmall' weight='bold' className='whitespace-nowrap'>
              {formatCurrency(balanceAvailable, currency, { hideCode: false, appendCode: true })}
            </Numeric>
          </FlexLayout>

          <FlexLayout spacing={4} alignItems='center' className='pr-16'>
            <Body color='secondary' size='xsmall' className='whitespace-nowrap'>
              {t('accountOverview.availableMargin.title')}
            </Body>
            <Numeric color='primary' size='xsmall' weight='bold' className='whitespace-nowrap'>
              {formatCurrency(availableMargin, currency, { hideCode: false, appendCode: true })}
            </Numeric>
          </FlexLayout>
        </FlexLayout>
      </OverlayScrollbarsComponent>
    </FlexLayout>
  );
};

export { MarginAccountDetailsCompact };
